import { SITE } from "../config/site";

export type SeoRobots = "index, follow" | "noindex, nofollow" | "noindex, follow";

export type SeoMeta = {
  title: string;
  description: string;
  robots: SeoRobots;
  canonicalPath: string;
  ogType: "website" | "article";
  /** When false, skip JSON-LD injection (e.g. authenticated app shells). */
  structuredData: boolean;
};

const BRAND = SITE.name;

const SEGMENT_LABELS: Record<string, string> = {
  home: "Home",
  events: "Events",
  new: "New event",
  artists: "Artists",
  venues: "Venues",
  profile: "Profile",
  guests: "Guest list",
  calendar: "Calendar",
  finance: "Finance",
  dashboard: "Dashboard",
  "command-center": "Command Center",
  logistics: "Logistics",
  invoices: "Invoices",
  workspace: "Command Center",
  overview: "Overview",
  live: "Live Ops",
  rsvp: "RSVP",
  team: "Team",
  pnl: "Finance",
  tickets: "Tickets",
  risk: "Risk",
};

function titleForWorkspace(product: "EventOS" | "P&L", segments: string[]): string {
  if (segments.length === 0) {
    return `${product} · ${BRAND}`;
  }

  const [a, b, c] = segments;

  if (a === "home") {
    return `Home · ${product} · ${BRAND}`;
  }

  if (a === "events" && b === "new") {
    return `New event · ${product} · ${BRAND}`;
  }

  if (a === "events" && segments.length === 1) {
    return `Events · ${product} · ${BRAND}`;
  }

  if (a === "events" && b && c) {
    if (c === "workspace") {
      const tab = segments[3];
      const label = tab ? SEGMENT_LABELS[tab] ?? tab : SEGMENT_LABELS.workspace;
      return `${label} · ${product} · ${BRAND}`;
    }
    const page = SEGMENT_LABELS[c] ?? c;
    return `${page} · ${product} · ${BRAND}`;
  }

  const head = SEGMENT_LABELS[a] ?? a;
  return `${head} · ${product} · ${BRAND}`;
}

function privateAppMeta(title: string, description: string): SeoMeta {
  return {
    title,
    description,
    robots: "noindex, nofollow",
    canonicalPath: "/",
    ogType: "website",
    structuredData: false,
  };
}

/** Resolve per-route SEO metadata (title, description, robots, canonical). */
export function resolveSeoMeta(pathname: string): SeoMeta {
  if (pathname === "/") {
    return {
      title: `${SITE.name} — ${SITE.tagline}`,
      description: SITE.description,
      robots: "index, follow",
      canonicalPath: "/",
      ogType: "website",
      structuredData: true,
    };
  }

  if (pathname === "/auth") {
    return privateAppMeta(
      `Sign in · ${BRAND}`,
      `Sign in to your ${BRAND} workspace. New access is by invitation — book a demo to join the early partner program.`,
    );
  }

  if (pathname === "/workspace") {
    return privateAppMeta(`Choose workspace · ${BRAND}`, `Select EventOS or P&L workspace in ${BRAND}.`);
  }

  if (pathname.startsWith("/i/")) {
    return {
      title: `Request an invitation · ${BRAND}`,
      description: "Invite-only event. Every request is reviewed personally.",
      robots: "noindex, nofollow",
      canonicalPath: pathname,
      ogType: "website",
      structuredData: false,
    };
  }

  if (pathname.startsWith("/rsvp/")) {
    return {
      title: `Event RSVP · ${BRAND}`,
      description: `Confirm your attendance for this ${BRAND} event.`,
      robots: "noindex, follow",
      canonicalPath: pathname,
      ogType: "website",
      structuredData: false,
    };
  }

  if (pathname === "/directory/guests" || pathname === "/guests") {
    return privateAppMeta(`Guest list · ${BRAND}`, `Promoter guest list in ${BRAND}.`);
  }

  const parts = pathname.split("/").filter(Boolean);
  if (parts[0] === "event-os") {
    return privateAppMeta(
      titleForWorkspace("EventOS", parts.slice(1)),
      `EventOS workspace in ${BRAND} — internal event operations.`,
    );
  }

  if (parts[0] === "pnl") {
    return privateAppMeta(
      titleForWorkspace("P&L", parts.slice(1)),
      `P&L workspace in ${BRAND} — internal financial dashboards.`,
    );
  }

  return privateAppMeta(BRAND, SITE.description);
}
